import { useState, useEffect, useCallback } from 'react';
import { Language } from '../config/translations';
import { detectLanguage } from '../utils/staticData';
import { createTranslationFunction } from '../utils/appUtils';
import { settingsService } from '../services/settingsService';

export interface UseLanguageManagementReturn {
  // State
  language: Language;
  setLanguage: React.Dispatch<React.SetStateAction<Language>>;
  t: (key: any) => string;
  
  // Actions
  loadUserLanguage: () => Promise<void>;
  saveUserLanguage: (newLanguage: Language) => Promise<void>;
}

export const useLanguageManagement = (
  isAuthenticated: boolean,
  user: any
): UseLanguageManagementReturn => {
  const [language, setLanguage] = useState<Language>(detectLanguage());
  
  // Translation helper
  const t = createTranslationFunction(language);

  // Load user language preference
  const loadUserLanguage = useCallback(async () => {
    const userLanguage = await settingsService.loadUserLanguage(isAuthenticated, user);
    if (userLanguage) {
      setLanguage(userLanguage);
    }
  }, [isAuthenticated, user]);

  // Save user language preference
  const saveUserLanguage = useCallback(async (newLanguage: Language) => {
    const success = await settingsService.saveUserLanguage(newLanguage, isAuthenticated, user);
    if (success || !isAuthenticated) {
      setLanguage(newLanguage);
    }
  }, [isAuthenticated, user]);

  // Load user language when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserLanguage();
    }
  }, [isAuthenticated, user, loadUserLanguage]);

  return {
    // State
    language,
    setLanguage,
    t,
    
    // Actions
    loadUserLanguage,
    saveUserLanguage,
  };
};